// Shared date-range presets for dashboard/report filters — current month,
// last month, last 3/6 months, current financial year (India: 1-Apr to
// 31-Mar, matching the JournalLedgerPage convention), and custom.

const toISO = (d) => d.toISOString().split('T')[0];
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const FY_START_MONTH = 3; // April, 0-indexed
const fyStartYearForDate = (d) => (d.getMonth() >= FY_START_MONTH ? d.getFullYear() : d.getFullYear() - 1);

export const DATE_RANGE_PRESETS = [
  { value: 'current_month', label: 'Current Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'current_fy', label: 'Current Financial Year' },
  { value: 'custom', label: 'Custom Range' },
];

/**
 * Resolve a preset key (+ optional {from, to} for 'custom') to a concrete
 * { from, to } ISO ("YYYY-MM-DD") date window. Ranges other than 'last_month'
 * run through today (not the calendar period's end), since these are "so far"
 * counts for a live dashboard.
 */
export function resolveDateRange(preset, custom = {}) {
  const today = new Date();
  switch (preset) {
    case 'current_month':
      return { from: toISO(startOfMonth(today)), to: toISO(today) };
    case 'last_month': {
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return { from: toISO(startOfMonth(lastMonth)), to: toISO(endOfMonth(lastMonth)) };
    }
    case 'last_3_months':
      return { from: toISO(new Date(today.getFullYear(), today.getMonth() - 2, 1)), to: toISO(today) };
    case 'last_6_months':
      return { from: toISO(new Date(today.getFullYear(), today.getMonth() - 5, 1)), to: toISO(today) };
    case 'current_fy':
      return { from: `${fyStartYearForDate(today)}-04-01`, to: toISO(today) };
    case 'custom':
      return { from: custom.from || '', to: custom.to || '' };
    default:
      return { from: toISO(startOfMonth(today)), to: toISO(today) };
  }
}

// ── Appointment-specific presets ──────────────────────────────────────
// Appointments can be booked for future dates, so unlike the registration
// presets above, single-day/week/month/FY windows here span their FULL
// natural period (not capped at today). Only the backward-looking "last N
// months" windows stay capped at today, since a look-back window running
// into the future wouldn't make sense.

const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};
const startOfWeekMonday = (d) => {
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(d, diff);
};

export const APPOINTMENT_DATE_RANGE_PRESETS = [
  { value: 'day_before_yesterday', label: 'Day Before Yesterday' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'day_after_tomorrow', label: 'Day After Tomorrow' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'this_fy', label: 'This Financial Year' },
  { value: 'custom', label: 'Custom Range' },
];

export function resolveAppointmentDateRange(preset, custom = {}) {
  const today = new Date();
  const singleDay = (d) => ({ from: toISO(d), to: toISO(d) });

  switch (preset) {
    case 'day_before_yesterday':
      return singleDay(addDays(today, -2));
    case 'yesterday':
      return singleDay(addDays(today, -1));
    case 'today':
      return singleDay(today);
    case 'tomorrow':
      return singleDay(addDays(today, 1));
    case 'day_after_tomorrow':
      return singleDay(addDays(today, 2));
    case 'this_week': {
      const start = startOfWeekMonday(today);
      return { from: toISO(start), to: toISO(addDays(start, 6)) };
    }
    case 'this_month':
      return { from: toISO(startOfMonth(today)), to: toISO(endOfMonth(today)) };
    case 'last_month': {
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return { from: toISO(startOfMonth(lastMonth)), to: toISO(endOfMonth(lastMonth)) };
    }
    case 'last_3_months':
      return { from: toISO(new Date(today.getFullYear(), today.getMonth() - 2, 1)), to: toISO(today) };
    case 'last_6_months':
      return { from: toISO(new Date(today.getFullYear(), today.getMonth() - 5, 1)), to: toISO(today) };
    case 'this_fy': {
      const startYear = fyStartYearForDate(today);
      return { from: `${startYear}-04-01`, to: `${startYear + 1}-03-31` };
    }
    case 'custom':
      return { from: custom.from || '', to: custom.to || '' };
    default:
      return singleDay(today);
  }
}
